import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { CatalogProvider } from "../../api/integrations";
import { useTrackEvent } from "../../libs/analytics";
import { Button } from "../../components/ui/button";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "../../components/ui/sheet";
import { cn } from "../../lib/utils";
import { StatusDot } from "../../pages/profile-settings/integrations-hub/status-badge";
import { needsReconnect } from "../../pages/profile-settings/integrations-hub/connection-health";
import { topNavGhostTextButtonClassName } from "../../ui/header/top-nav-button";
import {
  useDiscoveryCatalog,
  useDiscoveryCompletion,
} from "./use-integration-discovery";
import {
  configuredProviders,
  connectionHealth,
  connectionLabel,
  primaryProvider,
  statusPriority,
  unconfiguredProviders,
  INTEGRATIONS_PATH,
} from "./model";
import { DiscoveryLogo, ProviderIcons } from "./integration-status";

function IntegrationMenuRow({
  provider,
  onNavigate,
}: {
  provider: CatalogProvider;
  onNavigate: () => void;
}) {
  const navigate = useNavigate();
  const track = useTrackEvent();
  const health = connectionHealth(provider);
  const label = connectionLabel(provider);
  return (
    <button
      type="button"
      aria-label={`Open ${provider.displayName} integration`}
      className="flex w-full items-center justify-between gap-2 rounded-lg bg-white p-3 text-left text-xs leading-4 text-neutral-800 shadow-xs cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
      onClick={() => {
        if (needsReconnect(provider.connection!))
          track({
            eventName: "integration-discovery-reconnect",
            metadata: { placement: "topnav", provider: provider.key },
            useServices: ["AMP"],
          });
        navigate(`${INTEGRATIONS_PATH}/${provider.key}`);
        onNavigate();
      }}
    >
      <span className="flex min-w-0 items-center gap-2 font-medium">
        <DiscoveryLogo provider={provider} size={16} />
        <span className="truncate">{provider.displayName}</span>
      </span>
      <span className="flex shrink-0 items-center gap-2">
        <span className="flex items-center gap-1 text-neutral-600">
          <span
            className={cn(
              "inline-flex items-center [&>span]:shrink-0",
              health.status === "syncing"
                ? "[&>span]:size-4 [&_i]:text-xs"
                : "[&>span]:size-1"
            )}
          >
            <StatusDot {...health} />
          </span>
          <span>
            {health.status === "active" && label.startsWith("Synced ")
              ? `Healthy · ${label.slice("Synced ".length)}`
              : label}
          </span>
        </span>
        <i
          aria-hidden="true"
          className="fa-regular fa-arrow-right w-[15px] text-center"
        />
      </span>
    </button>
  );
}

export function IntegrationsMenu() {
  const { enabled, canManage, catalog } = useDiscoveryCatalog();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  useDiscoveryCompletion(catalog.data);
  if (!enabled || !catalog.data) return null;
  const connected = [
    ...configuredProviders(catalog.data),
    ...unconfiguredProviders(catalog.data),
  ];
  if (connected.length === 0) return null;
  const primary = primaryProvider(connected)!;
  const available = catalog.data.filter(
    (provider) => !provider.comingSoon && !provider.requiresUpgrade
  );
  const manage = () => {
    setOpen(false);
    navigate(INTEGRATIONS_PATH);
  };
  return (
    <Sheet open={open} onOpenChange={setOpen} modal={false}>
      <SheetTrigger asChild>
        <button
          type="button"
          className={cn(
            topNavGhostTextButtonClassName,
            "gap-1.5 px-2 data-[state=open]:bg-blue-50 data-[state=open]:text-blue-500"
          )}
          aria-label={`Open integrations: ${connected.map((p) => `${p.displayName}, ${connectionLabel(p)}`).join("; ")}`}
        >
          <ProviderIcons providers={connected} limit={2} iconSize={16} />
          <StatusDot {...connectionHealth(primary)} />
          {statusPriority(primary) <= 2 && (
            <span className="hidden text-xs sm:inline">
              {connectionLabel(primary)}
            </span>
          )}
        </button>
      </SheetTrigger>
      <SheetContent
        hideOverlay
        showCloseButton={false}
        className="flex w-[min(360px,100vw)] max-w-full flex-col gap-0 bg-blue-25 p-0 font-inter"
        aria-describedby={undefined}
      >
        <div className="flex h-[52px] shrink-0 items-center justify-between gap-2 border-b border-neutral-100 bg-blue-50 px-4 max-[391px]:pr-12">
          <SheetTitle className="font-gabarito text-base font-bold leading-5">
            Integrations
          </SheetTitle>
          <Button
            variant="outline"
            size="xs"
            className="h-6 rounded-lg leading-4 shadow-xs"
            onClick={manage}
          >
            Go to Integrations
            <i
              aria-hidden="true"
              className="fa-regular fa-arrow-right w-[15px] text-center"
            />
          </Button>
        </div>
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
          <section aria-label="Connected integrations" className="space-y-2">
            <p className="flex items-center gap-2 text-xs font-medium leading-4 uppercase text-neutral-600">
              <i
                aria-hidden="true"
                className="fa-regular fa-check w-[15px] text-center"
              />
              Connected
            </p>
            {connected.map((provider) => (
              <IntegrationMenuRow
                key={provider.key}
                provider={provider}
                onNavigate={() => setOpen(false)}
              />
            ))}
          </section>
          {/* Explicitly opened management UI is not a proactive discovery impression. */}
          <section aria-label="More integrations" className="space-y-3">
            <p className="flex items-center gap-2 text-xs font-medium leading-4 uppercase text-neutral-600">
              <i
                aria-hidden="true"
                className="fa-regular fa-plus w-[15px] text-center"
              />
              {canManage ? "Add more" : "Explore more"}
            </p>
            <div className="flex flex-col items-center gap-3 rounded-lg bg-white p-4 text-center text-neutral-800">
              <span
                aria-hidden="true"
                className="inline-flex size-10 shrink-0 items-center justify-center rounded-lg bg-blue-37 text-blue-500"
              >
                <i className="fa-regular fa-puzzle-piece text-base" />
              </span>
              <p className="w-full text-sm font-semibold leading-5">
                Connect more apps with integrations to import ideas and media.
              </p>
              <ProviderIcons providers={available} limit={7} />
              <Button
                variant="outline"
                size="sm"
                className="rounded-lg shadow-xs"
                onClick={manage}
              >
                {canManage ? "Connect integrations" : "View integrations"}
                <i
                  aria-hidden="true"
                  className={cn(
                    "fa-regular size-5 text-base leading-5",
                    canManage ? "fa-plus" : "fa-arrow-right"
                  )}
                />
              </Button>
            </div>
          </section>
        </div>
        <SheetClose asChild>
          <Button
            variant="ghost"
            size="icon-sm"
            className="absolute -left-8 top-4 rounded-r-none rounded-l-lg bg-white text-neutral-800 shadow-md max-[391px]:left-auto max-[391px]:right-2 max-[391px]:top-2.5 max-[391px]:rounded-lg"
            aria-label="Close integrations"
          >
            <i aria-hidden="true" className="fa-regular fa-xmark text-base" />
          </Button>
        </SheetClose>
      </SheetContent>
    </Sheet>
  );
}
